import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type AgentListeningState = {
  active: boolean
  sessionId: string | null
  profileId: string | null
  /** Bound agent thread for this session. Chip only shows when this thread is open. */
  supportThreadId?: string | null
  /** Session completed or room left — chip must not say Live. */
  ended?: boolean
  capability?: string
  /** Who/what the session is about, e.g. the candidate name. */
  label?: string
  /** Chip copy for the support mode, e.g. "Interview support". */
  modeLabel?: string
}

export type AgentListeningContextValue = AgentListeningState & {
  setListening: (next: Omit<AgentListeningState, 'active'> & { active?: boolean }) => void
  clearListening: () => void
}

const idle: AgentListeningState = {
  active: false,
  sessionId: null,
  profileId: null,
}

const AgentListeningContext = createContext<AgentListeningContextValue | null>(null)

export function AgentListeningProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AgentListeningState>(idle)

  const setListening = useCallback(
    (next: Omit<AgentListeningState, 'active'> & { active?: boolean }) => {
      setState({
        active: next.active ?? Boolean(next.sessionId),
        sessionId: next.sessionId,
        profileId: next.profileId,
        supportThreadId: next.supportThreadId ?? null,
        ended: next.ended ?? false,
        capability: next.capability,
        label: next.label,
        modeLabel: next.modeLabel,
      })
    },
    [],
  )

  const clearListening = useCallback(() => {
    setState(idle)
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      setListening,
      clearListening,
    }),
    [state, setListening, clearListening],
  )

  return (
    <AgentListeningContext.Provider value={value}>{children}</AgentListeningContext.Provider>
  )
}

export function useAgentListening(): AgentListeningContextValue {
  const ctx = useContext(AgentListeningContext)
  return (
    ctx ?? {
      ...idle,
      setListening: () => {},
      clearListening: () => {},
    }
  )
}
